import type { DeletedOrganizationItem } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { RiRefreshLine, RiErrorWarningLine } from '@remixicon/react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
  useDisclosure
} from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, ColumnsToggleDropdown, TableFilters } from '@/components/table';
import {
  deletedOrganizationsListParamsAtom,
  deletedOrganizationsQueryAtom,
  organizationsListParamsAtom,
  restoreOrganization
} from '@/store';
import { PermissionGuard } from '@/components/shared/PermissionGuard';

// Custom hooks
import { useDeletedOrganizationsSorting } from '@/hooks/deleted-organizations';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { SearchInput } from '@/components/shared/SearchInput';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { OrganizationCell } from '@/components/shared/OrganizationCell';
import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';

export default function OrganizationsDeletedPage() {
  const { data, isLoading, isError, error } = useAtomValue(deletedOrganizationsQueryAtom);
  const [params, setParams] = useAtom(deletedOrganizationsListParamsAtom);
  const [, setMainParams] = useAtom(organizationsListParamsAtom);

  const organizations = React.useMemo<DeletedOrganizationItem[]>(() => data?.data ?? [], [data]);

  // Search state
  const [searchTerm, setSearchTerm] = React.useState('');

  // Date filter state
  const [dateFilter, setDateFilter] = React.useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  // Custom hooks
  const { sortDescriptor, setSortDescriptor, sortedOrganizations } = useDeletedOrganizationsSorting(organizations);

  // Simple page change handler
  const handlePageChange = React.useCallback(
    (page: number) => {
      setParams((prev) => ({ ...prev, page }));
    },
    [setParams]
  );

  // Search handler
  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      search: searchTerm || undefined
    }));
  }, [searchTerm, setParams]);

  // Clear search handler
  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      search: undefined,
      refresh: Date.now() // Force refetch
    }));
  }, [setParams]);

  // Clear all filters handler
  const clearFilters = React.useCallback(() => {
    setSearchTerm('');
    setDateFilter({ start: null, end: null });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: {},
      refresh: Date.now() // Force refetch
    }));
  }, [setParams]);

  const handleDateChange = React.useCallback(
    (value: { start: Date | null; end: Date | null }) => {
      setDateFilter(value);

      let dateFilterValue = '';
      if (value.start && value.end) {
        const startDate = value.start.toISOString().split('T')[0];
        const endDate = value.end.toISOString().split('T')[0];
        dateFilterValue = `${startDate},${endDate}`;
      } else if (value.start) {
        dateFilterValue = value.start.toISOString().split('T')[0];
      }

      setParams((prev) => ({
        ...prev,
        page: 1,
        filters: {
          ...prev.filters,
          date: dateFilterValue || undefined
        }
      }));
    },
    [setParams]
  );

  const hasAnyFilter = React.useMemo(() => {
    return Boolean(searchTerm) || Boolean(dateFilter.start || dateFilter.end);
  }, [searchTerm, dateFilter]);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Restore modal
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [restoringId, setRestoringId] = React.useState<number | null>(null);
  const [isRestoring, setIsRestoring] = React.useState(false);

  const requestRestore = React.useCallback(
    (id: number) => {
      setRestoringId(id);
      onOpen();
    },
    [onOpen]
  );

  const confirmRestore = React.useCallback(async () => {
    if (!restoringId) return;

    try {
      setIsRestoring(true);
      const response = await restoreOrganization(restoringId);

      // Show success message
      if ((response as any)?.meta?.message) {
        const { toast } = await import('react-toastify');
        toast.success((response as any).meta.message);
      }

      // Trigger refresh to update UI
      triggerRefresh();
      // Also refresh main organizations cache
      setMainParams((prev) => ({ ...prev, refresh: Date.now() }));

      // Close modal and reset state
      setIsRestoring(false);
      onOpenChange();
      setRestoringId(null);
    } catch (error) {
      setIsRestoring(false);
      // Error is handled by axios interceptor
    }
  }, [restoringId, onOpenChange, triggerRefresh, setMainParams]);

  const renderCell = React.useCallback(
    (item: DeletedOrganizationItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name': {
          return <OrganizationCell organization={item} showDescription />;
        }
        case 'user':
          return item.user ? <UserCell user={item.user} linked /> : <span>-</span>;
        case 'created_at':
          return <DateTimeDisplay dateString={item.created_at} />;
        case 'actions':
          return (
            <div className="flex justify-end">
              <PermissionGuard permission="Organization.update">
                <Tooltip content={i18n.t('copy.restore_workspace')}>
                  <Button isIconOnly color="warning" size="sm" variant="light" onPress={() => requestRestore(item.id)}>
                    <RiRefreshLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [requestRestore]
  );

  const renderCollapseContent = React.useCallback((item: DeletedOrganizationItem) => {
    const formatUser = (user: any) => {
      if (!user) return null;
      return {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email
      };
    };

    return (
      <div className="space-y-6 p-4">
        {/* Timestamps */}
        <div className="grid grid-cols-3 gap-4">
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">{i18n.t('columns.createdAt')}</p>
            <DateTimeDisplay dateString={item.created_at} showSeconds />
            {formatUser(item.created_by) && <UserCell user={formatUser(item.created_by)!} dense showEmail={false} />}
          </div>
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">{i18n.t('copy.updated_at')}</p>
            <DateTimeDisplay dateString={item.updated_at} showSeconds />
            {formatUser(item.updated_by) && <UserCell user={formatUser(item.updated_by)!} dense showEmail={false} />}
          </div>
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">{i18n.t('copy.deleted_at')}</p>
            <DateTimeDisplay dateString={item.deleted_at!} showSeconds />
            {formatUser(item.deleted_by) && <UserCell user={formatUser(item.deleted_by)!} dense showEmail={false} />}
          </div>
        </div>
      </div>
    );
  }, []);

  const columns = React.useMemo(
    () => [
      { name: i18n.t('copy.workspace'), uid: 'name', sortable: true },
      { name: i18n.t('copy.owner'), uid: 'user', sortable: true },
      { name: i18n.t('columns.createdAt'), uid: 'created_at', sortable: true },
      { name: i18n.t('common.actions'), uid: 'actions', align: 'end', cellClass: 'text-right' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedOrganizations}
      emptyMessage={i18n.t('copy.no_deleted_workspaces_found')}
      error={error}
      errorMessage={i18n.t('copy.failed_to_load_deleted_workspaces')}
      enableCollapse={true}
      renderCollapseContent={renderCollapseContent}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
        >
          <SearchInput
            placeholder={i18n.t('copy.search_workspaces')}
            value={searchTerm}
            onValueChange={setSearchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <DateRangeFilter label={i18n.t('copy.deleted_date')} value={dateFilter} onChange={handleDateChange} />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={(sortDescriptor || { column: 'name', direction: 'ascending' }) as any}
      title={i18n.t('copy.deleted_workspaces')}
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) =>
        setSortDescriptor({
          column: d.column as keyof DeletedOrganizationItem,
          direction: d.direction as 'ascending' | 'descending'
        })
      }
    >
      {/* Restore modal */}
      <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <RiErrorWarningLine className="text-warning h-5 w-5" />
                <span>{i18n.t('copy.restore_workspace')}</span>
              </ModalHeader>
              <ModalBody>
                <p>Are you sure you want to restore this workspace?</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
                <Button color="success" isLoading={isRestoring} onPress={confirmRestore}>
                  Restore
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </TablePage>
  );
}
